import type { MarketEstimate, MarketRefreshOutcome } from "@hobbyshopify/backend-client";
import { formatWhen } from "./format";

/** A market refresh a person asked for, as the screen shows it. */
export type MarketRefreshStatus =
  | { readonly kind: "idle" }
  | { readonly kind: "busy" }
  | { readonly kind: "done"; readonly outcome: MarketRefreshOutcome }
  | { readonly kind: "error"; readonly message: string };

export const idleMarketRefresh: MarketRefreshStatus = { kind: "idle" };

/**
 * - `unavailable`: no source knows the product, or it never answered for it.
 * - `stale`: the last answer is an hour old or more.
 * - `fresh`: retrieved within the hour.
 */
export type MarketFreshness = "unavailable" | "stale" | "fresh";

export function marketFreshness(estimate: MarketEstimate | null | undefined): MarketFreshness {
  if (!estimate?.retrievedAt) return "unavailable";
  return estimate.stale ? "stale" : "fresh";
}

export function marketFreshnessText(estimate: MarketEstimate | null | undefined): string {
  if (!estimate) return "No pricing source knows this product, so there is no market price.";
  if (!estimate.retrievedAt) return "No market price retrieved yet.";
  const when = formatWhen(estimate.retrievedAt);
  return estimate.stale ? `Out of date: retrieved ${when}.` : `Retrieved ${when}.`;
}

const FAILURE_TEXT: Record<NonNullable<MarketEstimate["lastFailureReason"]>, string> = {
  unavailable: "The pricing source could not be reached.",
  empty: "The pricing source has no price for this product.",
  not_found: "The pricing source no longer knows this product.",
};

/** Why the latest refresh did not land, while it is the latest word; null otherwise. */
export function marketFailureText(estimate: MarketEstimate | null | undefined): string | null {
  if (!estimate?.lastFailureAt || !estimate.lastFailureReason) return null;
  if (estimate.retrievedAt && estimate.retrievedAt >= estimate.lastFailureAt) return null;
  const kept = estimate.marketCents != null ? " The last known price is shown." : "";
  return `${FAILURE_TEXT[estimate.lastFailureReason]}${kept}`;
}

export function refreshOutcomeText(outcome: MarketRefreshOutcome): string {
  switch (outcome) {
    case "refreshed":
      return "Market price refreshed.";
    case "fresh":
      return "The market price is already current.";
    case "cooldown":
      return "Refreshed under a minute ago. Try again shortly.";
    case "empty":
      return "The source answered with no price. The previous price stands.";
    case "failed":
      return "Couldn't refresh the market price. The previous price stands.";
    case "pending":
      return "Still refreshing. Check back in a moment.";
  }
}
